/**
 * Tâche 1435, étape 2 (Eric 2026-10-02) — Carte « Source web » d'un document
 * importé par aspiration d'URL.
 *
 * > « Tu vas ajouter un bouton pour permettre une mise-à-jour manuelle des
 * >   informations sur cette page, car le formateur peut vouloir mettre à jour
 * >   le RAG et le chatbot, quand il découvre que la page a évolué. »
 *
 * Choix d'Eric sur le geste : **ré-aspirer, MONTRER ce qui a changé, puis
 * demander confirmation**. La mise à jour se fait donc en deux temps — le
 * premier appel n'écrit rien (`confirmer` absent), le second applique.
 * Écrire d'abord et montrer ensuite ferait du bouton une opération
 * irréversible sur le RAG et le chatbot.
 *
 * La carte DIT aussi ce que cette source ne produit pas : sans la phrase, un
 * formateur chercherait indéfiniment les Q&R d'une page web.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowSquareOut, ArrowsClockwise, Globe, Info, SpinnerGap } from "@phosphor-icons/react";
import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

const dateLisible = (iso, lang) => {
  if (!iso) return lang === "fr" ? "jamais" : "never";
  try {
    return new Date(iso).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export const SourceWebCard = ({ video, onUpdated }) => {
  const { lang, tr } = useLang();
  const [occupe, setOccupe] = useState(false);
  const [diff, setDiff] = useState(null);
  // Repli « coller le texte » (Eric 2026-10-04) pour les pages inaspirables.
  const [collage, setCollage] = useState("");
  const [collageMotif, setCollageMotif] = useState("");

  if (!video || video.source_kind !== "url" || !video.source_url) return null;

  const aspirer = async () => {
    setOccupe(true);
    try {
      const { data } = await axios.post(
        `${API}/api/text-documents/${video.id}/reaspirer`,
        { texte_colle: collage.trim() || null },
        { withCredentials: true },
      );
      setDiff(data);
      setCollageMotif("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.collage_possible) {
        setCollageMotif(`${detail.message} ${detail.aide || ""}`.trim());
      } else {
        toast.error(typeof detail === "string" ? detail
          : tr("Aspiration impossible.", "Could not fetch the page."));
      }
    } finally {
      setOccupe(false);
    }
  };

  const appliquer = async () => {
    setOccupe(true);
    try {
      const { data } = await axios.post(
        `${API}/api/text-documents/${video.id}/reaspirer?confirmer=true`,
        { texte_colle: collage.trim() || null },
        { withCredentials: true },
      );
      // Les SEULES connaissances permises sur une source de référence. Lancer
      // les Q&R ici recevrait un refus du serveur (portée 1435).
      const regen = data?.a_regenerer || [];
      if (regen.includes("tags")) {
        try {
          await axios.post(`${API}/api/videos/${video.id}/tags/suggest`, {},
            { withCredentials: true });
        } catch { /* proposition de tags non bloquante */ }
      }
      if (regen.includes("synthese")) {
        try {
          await axios.post(`${API}/api/videos/${video.id}/synthesis/generate?background=1`, {},
            { withCredentials: true });
        } catch { /* la synthèse peut être relancée à la main */ }
      }
      if (regen.includes("concepts")) {
        try {
          await axios.post(`${API}/api/agent/generate-concepts-batch/${video.id}?background=1`, {},
            { withCredentials: true });
        } catch { /* les concepts peuvent être relancés à la main */ }
      }
      setDiff(null);
      setCollage("");
      setCollageMotif("");
      toast.success(tr(
        "Page mise à jour · synthèse, concepts et étiquettes relancés.",
        "Page updated · summary, concepts and tags re-launched.",
      ));
      if (typeof onUpdated === "function") await onUpdated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : tr("Mise à jour impossible.", "Could not apply the update."));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div
      className="bg-white border border-sky-200 rounded-xl p-4 space-y-3"
      data-testid="source-web-card"
    >
      <div className="flex items-start gap-2">
        <Globe size={18} weight="duotone" className="text-sky-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-sky-800">
            {tr("Source : page web aspirée", "Source: scraped web page")}
          </p>
          <a
            href={video.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#002FA7] hover:underline break-all inline-flex items-start gap-1"
            data-testid="source-web-url"
          >
            {video.source_url}
            <ArrowSquareOut size={12} className="flex-shrink-0 mt-1" />
          </a>
          <p className="text-[11px] text-zinc-500 mt-1" data-testid="source-web-fetched-at">
            {tr("Dernière aspiration : ", "Last fetched: ")}
            {dateLisible(video.source_fetched_at, lang)}
            {video.source_revision > 1 && (
              <span className="ml-1 text-zinc-400">
                {tr(`· révision ${video.source_revision}`, `· revision ${video.source_revision}`)}
              </span>
            )}
          </p>
        </div>
      </div>

      <p className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-2 flex items-start gap-1.5"
         data-testid="source-web-portee">
        <Info size={13} className="text-zinc-400 flex-shrink-0 mt-0.5" />
        <span>
          {tr(
            "Une page web ne produit que le titre, les étiquettes, une synthèse et les concepts. Aucune Q&R, procédure, script ou objection n'est générée depuis cette source.",
            "A web page only yields the title, the tags, a summary and the concepts. No Q&A, procedure, script or objection is generated from this source.",
          )}
        </span>
      </p>

      {video.source_mode === "collage" && (
        <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2"
           data-testid="source-web-mode-collage">
          {tr(
            "Texte COLLÉ à la main : cette page n'est pas lisible automatiquement (JavaScript ou protection anti-robot). La mise à jour redemandera un collage.",
            "Text PASTED by hand: this page cannot be read automatically (JavaScript or anti-bot protection). Updating will ask for a new paste.",
          )}
        </p>
      )}

      {collageMotif && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-2 space-y-2" data-testid="source-web-collage">
          <p className="text-[11px] text-amber-900 leading-snug" data-testid="source-web-collage-motif">
            {collageMotif}
          </p>
          <textarea
            value={collage}
            onChange={(e) => setCollage(e.target.value)}
            rows={5}
            placeholder={tr(
              "Collez ici le texte à jour de la page (30 caractères au minimum)…",
              "Paste the up-to-date page text here (30 characters minimum)…",
            )}
            className="w-full text-xs p-2 border border-amber-300 rounded bg-white outline-none focus:border-[#002FA7]"
            data-testid="source-web-collage-input"
          />
          <button
            type="button"
            onClick={aspirer}
            disabled={collage.trim().length < 30 || occupe}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#002FA7] text-white text-xs font-semibold hover:bg-[#001f70] disabled:opacity-50"
            data-testid="source-web-collage-submit"
          >
            {occupe && <SpinnerGap size={14} className="animate-spin" />}
            {tr("Comparer le texte collé", "Compare the pasted text")}
          </button>
        </div>
      )}

      {!diff && !collageMotif && (
        <button
          type="button"
          onClick={aspirer}
          disabled={occupe}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#002FA7] text-white text-xs font-semibold hover:bg-[#001f70] disabled:opacity-50"
          data-testid="source-web-refresh-btn"
        >
          {occupe ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
          {occupe
            ? tr("Aspiration en cours…", "Fetching…")
            : tr("Mettre à jour depuis la page web", "Update from the web page")}
        </button>
      )}

      {diff && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="source-web-diff">
          <p className="text-xs font-bold text-amber-900" data-testid="source-web-diff-title">
            {diff.identique
              ? tr("La page n'a pas changé depuis la dernière aspiration.",
                   "The page has not changed since the last fetch.")
              : tr("La page a changé — voici ce qui diffère.",
                   "The page has changed — here is what differs.")}
          </p>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <span className="text-emerald-800 font-semibold" data-testid="source-web-diff-added">
              {tr(`+ ${diff.lignes_ajoutees} ligne(s) ajoutée(s)`, `+ ${diff.lignes_ajoutees} line(s) added`)}
            </span>
            <span className="text-rose-800 font-semibold" data-testid="source-web-diff-removed">
              {tr(`− ${diff.lignes_retirees} ligne(s) retirée(s)`, `− ${diff.lignes_retirees} line(s) removed`)}
            </span>
          </div>
          <p className="text-[10px] text-amber-800">
            {tr(
              `${diff.caracteres_avant} → ${diff.caracteres_apres} caractères`,
              `${diff.caracteres_avant} → ${diff.caracteres_apres} characters`,
            )}
          </p>
          {(diff.apercu_ajoutees?.length > 0 || diff.apercu_retirees?.length > 0) && (
            <div className="max-h-40 overflow-y-auto bg-white border border-amber-200 rounded p-2 space-y-0.5"
                 data-testid="source-web-diff-preview">
              {(diff.apercu_retirees || []).map((l, i) => (
                <p key={`r${i}`} className="text-[10px] text-rose-700 font-mono break-words">− {l}</p>
              ))}
              {(diff.apercu_ajoutees || []).map((l, i) => (
                <p key={`a${i}`} className="text-[10px] text-emerald-700 font-mono break-words">+ {l}</p>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={appliquer}
              disabled={occupe}
              className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="source-web-apply-btn"
            >
              {occupe && <SpinnerGap size={14} className="animate-spin" />}
              {tr("Appliquer et régénérer", "Apply and regenerate")}
            </button>
            <button
              type="button"
              onClick={() => setDiff(null)}
              disabled={occupe}
              className="flex-1 min-w-[100px] px-3 py-2 rounded-lg border border-zinc-300 bg-white text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
              data-testid="source-web-cancel-btn"
            >
              {tr("Annuler", "Cancel")}
            </button>
          </div>
          <p className="text-[10px] text-amber-800">
            {tr(
              "Appliquer réécrit le texte du document, puis relance la synthèse, les concepts et les étiquettes proposées (RAG et chatbot à jour).",
              "Applying rewrites the document text, then re-launches the summary, the concepts and the suggested tags (RAG and chatbot up to date).",
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default SourceWebCard;
